// Project theme — the top of the page Theme tab.
//
// Every page is meant to wear a PROJECT theme (`Page_Design/themes/<id>.json`)
// so a site looks the same everywhere. A page binds with `theme.ref`; the
// backend writes the theme's values into the page on load / save / publish.
// This panel binds, switches, detaches, saves the page's look as a new theme,
// and decides where the editor's changes go: to the WHOLE theme (every bound
// page follows) or to this page only (`theme.local`).

import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  createPageTheme, listPageThemes, updatePageTheme,
  type ProjectTheme, type ThemeOverview,
} from '../../../api/pageThemes'
import type { ColorScheme, DesignTokens, PageTheme } from './types'

const BODY_KEYS = ['preset', 'palette', 'typography', 'layout', 'darkMode'] as const
type BodyKey = typeof BODY_KEYS[number]
export type ThemeScope = 'theme' | 'page'

/** The look-defining part of a page theme (no binding keys). */
export function themeBody(t: PageTheme | undefined): PageTheme {
  const out: Record<string, unknown> = {}
  for (const k of BODY_KEYS) {
    const v = (t as Record<string, unknown> | undefined)?.[k]
    if (v !== undefined && v !== null && !(typeof v === 'object' && Object.keys(v as object).length === 0)) out[k] = v
  }
  return out as PageTheme
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/** What `next` changes relative to the theme — a page's `theme.local`. */
export function diffBody(next: PageTheme, base: PageTheme): PageTheme {
  const out: Record<string, unknown> = {}
  for (const k of BODY_KEYS) {
    const nv = (next as Record<string, unknown>)[k]
    const bv = (base as Record<string, unknown>)[k]
    if (k === 'preset') {
      if (nv && nv !== bv) out[k] = nv
      continue
    }
    if (!nv || typeof nv !== 'object') continue
    const changed: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(nv as Record<string, unknown>)) {
      if (!same(val, (bv as Record<string, unknown> | undefined)?.[key])) changed[key] = val
    }
    if (Object.keys(changed).length) out[k] = changed
  }
  return out as PageTheme
}

/** `body` with the page's local departures put back to the theme's values —
 *  what the THEME should become when the author edits in "whole theme" mode. */
function withoutLocal(body: PageTheme, local: PageTheme | undefined, base: PageTheme): PageTheme {
  if (!local) return body
  const out = JSON.parse(JSON.stringify(body)) as Record<string, Record<string, unknown> | string>
  for (const k of BODY_KEYS) {
    const lv = (local as Record<string, unknown>)[k]
    if (lv === undefined) continue
    const bv = (base as Record<string, unknown>)[k]
    if (k === 'preset') {
      if (bv) out[k] = bv as string
      continue
    }
    const grp = { ...((out[k] as Record<string, unknown>) || {}) }
    for (const key of Object.keys(lv as object)) {
      const orig = (bv as Record<string, unknown> | undefined)?.[key]
      if (orig === undefined) delete grp[key]
      else grp[key] = orig
    }
    out[k] = grp
  }
  return out as PageTheme
}

/** A page's designTokens with the theme's schemes swapped in (`fromTheme`),
 *  or — `schemes === null`, a detach — kept as the page's own. */
export function withThemeSchemes(tokens: DesignTokens | undefined, schemes: ColorScheme[] | null): DesignTokens {
  const all = tokens?.colorSchemes || []
  if (schemes === null) {
    return { ...(tokens || {}), colorSchemes: all.map(({ fromTheme: _f, ...s }) => s as ColorScheme) }
  }
  const ids = new Set(schemes.map((s) => s.id))
  const own = all.filter((s) => !s.fromTheme && !ids.has(s.id))
  return { ...(tokens || {}), colorSchemes: [...own, ...schemes.map((s) => ({ ...s, fromTheme: true }))] }
}

export function useProjectThemes(project: string) {
  const [overview, setOverview] = useState<ThemeOverview | null>(null)
  const [error, setError] = useState('')
  const reload = useCallback(async () => {
    if (!project) return
    try {
      setOverview(await listPageThemes(project))
      setError('')
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Could not load the project themes.')
    }
  }, [project])
  useEffect(() => { void reload() }, [reload])
  const patchDoc = useCallback((id: string, theme: PageTheme) => {
    setOverview((o) => o && { ...o, themes: o.themes.map((t) => (t.id === id ? { ...t, theme } : t)) })
  }, [])
  return { overview, error, reload, patchDoc }
}

/** Route a ThemeEditor change for a page bound to a project theme. Returns
 *  the page theme to emit; queues a theme save in "whole theme" scope. */
export function useThemeRouting(project: string, theme: PageTheme | undefined,
                                doc: ProjectTheme | undefined, scope: ThemeScope,
                                patchDoc: (id: string, t: PageTheme) => void,
                                reload: () => Promise<void>) {
  const timer = useRef<number | null>(null)
  const [status, setStatus] = useState('')
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])

  return {
    status,
    route(next: PageTheme): PageTheme {
      if (!theme?.ref || !doc) return next
      const body = themeBody(next)
      const binding: PageTheme = { ref: theme.ref, ...(theme.inherited ? { inherited: true } : {}) }
      if (scope === 'page') {
        const local = diffBody(body, doc.theme)
        return { ...body, ...binding, ...(Object.keys(local).length ? { local } : {}) }
      }
      const newThemeBody = withoutLocal(body, theme.local, doc.theme)
      patchDoc(doc.id, newThemeBody)
      if (timer.current) window.clearTimeout(timer.current)
      setStatus('Saving to the theme…')
      timer.current = window.setTimeout(async () => {
        try {
          const res = await updatePageTheme(project, doc.id, { theme: newThemeBody, replace: true })
          const others = res.synced.length
          setStatus(`Saved to theme “${doc.name}”${others ? ` — ${others} page${others === 1 ? '' : 's'} updated` : ''}.`)
          void reload()
        } catch (e) {
          const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          setStatus('')
          toast.error(msg || 'Could not save the theme change.')
        }
      }, 700)
      return { ...body, ...binding, ...(theme.local ? { local: theme.local } : {}) }
    },
  }
}

const LABEL: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)', marginBottom: 4, display: 'block',
}
const HINT: React.CSSProperties = { margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }
const BTN: React.CSSProperties = {
  padding: '5px 10px', fontSize: '0.8125rem', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
}
const SELECT: React.CSSProperties = { ...BTN, width: '100%', padding: '6px 8px', fontSize: '0.8625rem' }

function Swatches({ theme }: { theme: PageTheme }) {
  const p = theme.palette || {}
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {[p.primary, p.accent, p.background, p.text].map((c, i) => (
        <span key={i} style={{ width: 12, height: 12, borderRadius: 3, background: c || '#ccc', border: '1px solid rgba(0,0,0,0.12)' }} />
      ))}
    </span>
  )
}

interface PanelProps {
  project: string
  theme: PageTheme | undefined
  overview: ThemeOverview | null
  error: string
  scope: ThemeScope
  setScope: (s: ThemeScope) => void
  status: string
  reload: () => Promise<void>
  /** Bind (schemes = the theme's) or detach (schemes = null). */
  onBind: (theme: PageTheme, schemes: ColorScheme[] | null) => void
}

export default function ProjectThemePanel({ project, theme, overview, error, scope, setScope, status, reload, onBind }: PanelProps) {
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const themes = overview?.themes || []
  const ref = theme?.ref
  const doc = ref ? themes.find((t) => t.id === ref) : undefined

  const bind = (id: string) => {
    const d = themes.find((t) => t.id === id)
    if (!d) return
    onBind({ ref: d.id, ...themeBody(d.theme) }, d.colorSchemes || [])
  }

  const detach = () => {
    const { ref: _r, local: _l, inherited: _i, ...rest } = theme || {}
    onBind(rest, null)
  }

  const saveAsTheme = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      const d = await createPageTheme(project, { name: name.trim(), theme: themeBody(theme) })
      await reload()
      onBind({ ref: d.id, ...themeBody(d.theme) }, d.colorSchemes || [])
      toast.success(`Theme “${d.name}” created — this page now uses it.`)
      setNaming(false)
      setName('')
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Could not create the theme.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 14, borderBottom: '1px solid var(--color-border)' }}>
      <div style={LABEL}>Project theme</div>
      {error && <p style={{ ...HINT, color: 'var(--color-error)' }}>{error}</p>}

      {ref && !doc && overview && (
        <p style={{ ...HINT, color: 'var(--color-warning, #b45309)' }}>
          This page names the theme “{ref}”, which no longer exists. It keeps its current look —
          choose another theme or detach it.
        </p>
      )}

      {doc ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1.5px solid var(--color-accent)', borderRadius: 8, background: 'rgba(76,141,255,0.06)' }}>
            <Swatches theme={doc.theme} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8825rem', fontWeight: 600 }}>{doc.name}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                {theme?.inherited ? 'From the website · ' : ''}
                {(doc.usage?.pages.length ?? 0)} page{(doc.usage?.pages.length ?? 0) === 1 ? '' : 's'}
                {doc.usage?.sites.length ? ` · website ${doc.usage.sites.join(', ')}` : ''}
                {doc.colorSchemes?.length ? ` · ${doc.colorSchemes.length} band look${doc.colorSchemes.length === 1 ? '' : 's'}` : ''}
              </div>
            </div>
          </div>
          <div role="radiogroup" aria-label="Where changes go" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: '0.8225rem', cursor: 'pointer' }}>
              <input type="radio" checked={scope === 'theme'} onChange={() => setScope('theme')} />
              <span>Change the theme — every page using “{doc.name}” follows</span>
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: '0.8225rem', cursor: 'pointer' }}>
              <input type="radio" checked={scope === 'page'} onChange={() => setScope('page')} />
              <span>This page only (a departure from the theme)</span>
            </label>
          </div>
          {theme?.local && Object.keys(theme.local).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ ...HINT, flex: 1 }}>This page departs from the theme in: {Object.keys(theme.local).join(', ')}.</p>
              <button type="button" style={BTN} onClick={() => onBind({ ref: doc.id, ...(theme.inherited ? { inherited: true } : {}), ...themeBody(doc.theme) }, doc.colorSchemes || [])}>
                Reset to theme
              </button>
            </div>
          )}
          {status && <p style={HINT}>{status}</p>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select style={{ ...SELECT, width: 'auto', flex: 1 }} value="" onChange={(e) => e.target.value && bind(e.target.value)}>
              <option value="">Switch theme…</option>
              {themes.filter((t) => t.id !== doc.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button type="button" style={BTN} onClick={detach} title="Keep this look, but stop following the theme">Detach</button>
          </div>
        </>
      ) : (
        <>
          <p style={HINT}>
            {themes.length
              ? 'This page has its own look. Use a project theme so it matches the rest of the project.'
              : 'The project has no theme yet. Save this page’s look as one, then use it on every page.'}
          </p>
          {themes.length > 0 && (
            <select style={SELECT} value="" onChange={(e) => e.target.value && bind(e.target.value)}>
              <option value="">Use a project theme…</option>
              {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {naming ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Theme name"
                     onKeyDown={(e) => { if (e.key === 'Enter') void saveAsTheme() }}
                     style={{ ...SELECT, flex: 1, cursor: 'text' }} />
              <button type="button" style={BTN} disabled={busy || !name.trim()} onClick={() => void saveAsTheme()}>Save</button>
              <button type="button" style={BTN} onClick={() => setNaming(false)}>Cancel</button>
            </div>
          ) : (
            <button type="button" style={{ ...BTN, alignSelf: 'flex-start' }} onClick={() => setNaming(true)}>
              Save this look as a project theme…
            </button>
          )}
        </>
      )}
    </section>
  )
}
